/**
 * Step 6: Policies & Rules
 * Configure entry notice, subletting, pet policy, parking, utilities,
 * short-term rental prohibition, guest policy, and alterations policy
 */

import { useEffect } from 'react';
import type { WizardState, JurisdictionRules } from '../../../types/lease-generation';
import { RequiredMark } from '../RequiredMark';

interface Step6Props {
  state: WizardState;
  updateInputs: (partial: Partial<WizardState['inputs']>) => void;
  setShortTermRentalProhibited: (prohibited: boolean) => void;
  updateSmokingPolicy: (partial: Partial<WizardState['smokingPolicy']>) => void;
  updateRentersInsurance: (partial: Partial<WizardState['rentersInsurance']>) => void;
}

export function Step6PoliciesRules({
  state,
  updateInputs,
  setShortTermRentalProhibited,
  updateSmokingPolicy,
  updateRentersInsurance,
}: Step6Props) {
  const { inputs, jurisdictionInfo, shortTermRentalProhibited, smokingPolicy, rentersInsurance } = state;

  const rules = jurisdictionInfo?.rules as JurisdictionRules | undefined;
  const minEntryHours = rules?.notice_periods?.entry_hours_minimum ?? null;

  // Default entry notice to jurisdiction minimum or 24 hours
  useEffect(() => {
    const defaults: Partial<WizardState['inputs']> = {};
    if (inputs.entryNoticeHours == null) {
      defaults.entryNoticeHours = Math.max(minEntryHours ?? 0, 24);
    }
    if (inputs.sublettingPolicy == null) {
      defaults.sublettingPolicy = 'not-allowed';
    }
    if (inputs.guestPolicy == null) {
      defaults.guestPolicy = 'Guests may stay for a maximum of 14 consecutive days or 30 days total in any 12-month period without prior written approval from the Landlord. Overnight guests exceeding these limits may be considered unauthorized occupants.';
    }
    if (inputs.alterationsPolicy == null) {
      defaults.alterationsPolicy = 'Tenant shall not make any alterations, additions, or improvements to the premises without the prior written consent of the Landlord. Minor decorative changes such as hanging pictures with small nails are permitted, provided Tenant restores the premises to its original condition upon move-out.';
    }
    if (Object.keys(defaults).length > 0) {
      updateInputs(defaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entryNoticeHours = inputs.entryNoticeHours ?? '';
  const sublettingPolicy = inputs.sublettingPolicy ?? 'not-allowed';
  const guestPolicy = inputs.guestPolicy ?? '';
  const alterationsPolicy = inputs.alterationsPolicy ?? '';

  const inputClass =
    'w-full px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring';
  const labelClass = 'block mb-1 text-sm font-medium text-foreground';
  const textAreaClass =
    'w-full px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y min-h-[80px]';
  const sectionClass = 'border border-border rounded-lg p-4';

  const sublettingOptions: Array<{ value: WizardState['inputs']['sublettingPolicy']; label: string; testId: string }> = [
    { value: 'not-allowed', label: 'Not Allowed', testId: 'subletting-policy-not-allowed' },
    { value: 'allowed', label: 'Allowed', testId: 'subletting-policy-allowed' },
    { value: 'with-approval', label: 'With Approval', testId: 'subletting-policy-with-approval' },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Policies & Rules</h3>
        <p className="text-sm text-muted-foreground">Configure policies for the property and lease.</p>
      </div>

      {/* Entry Notice Hours */}
      <div>
        <label htmlFor="entry-notice-hours" className={labelClass}>
          Entry Notice (hours) <RequiredMark />
        </label>
        <input
          id="entry-notice-hours"
          type="number"
          data-testid="entry-notice-hours"
          value={entryNoticeHours}
          min={minEntryHours ?? 1}
          step={1}
          onChange={e => updateInputs({ entryNoticeHours: e.target.value ? Number(e.target.value) : undefined })}
          className={inputClass}
          placeholder="e.g. 48"
          required
        />
        {minEntryHours != null && (
          <p className="mt-1 text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2">
            {jurisdictionInfo?.displayName} requires minimum {minEntryHours}-hour entry notice.
          </p>
        )}
      </div>

      {/* Subletting Policy */}
      <div>
        <label className={labelClass}>Subletting Policy</label>
        <div className="flex gap-3">
          {sublettingOptions.map(({ value, label, testId }) => (
            <button
              key={value}
              type="button"
              data-testid={testId}
              onClick={() => updateInputs({ sublettingPolicy: value })}
              className={`flex-1 px-3 py-2.5 text-sm font-medium border rounded-lg min-h-[44px] ${
                sublettingPolicy === value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-foreground border-border hover:bg-accent'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Short-Term Rental Prohibition */}
      <div className={sectionClass}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-foreground">Short-Term Rental Prohibition</div>
            <div className="text-xs text-muted-foreground">Prohibit listing on Airbnb, VRBO, and similar platforms?</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={shortTermRentalProhibited}
            data-testid="short-term-rental-toggle"
            onClick={() => setShortTermRentalProhibited(!shortTermRentalProhibited)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
              shortTermRentalProhibited ? 'bg-primary' : 'bg-input'
            }`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ${
              shortTermRentalProhibited ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </button>
        </div>
      </div>

      {/* Smoking Policy */}
      <div className={sectionClass}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-foreground">Smoking Prohibition</div>
            <div className="text-xs text-muted-foreground">Prohibit smoking inside the premises and common areas?</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={smokingPolicy.prohibited}
            data-testid="smoking-prohibited-toggle"
            onClick={() => updateSmokingPolicy({ prohibited: !smokingPolicy.prohibited, allowedAreas: undefined })}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
              smokingPolicy.prohibited ? 'bg-primary' : 'bg-input'
            }`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ${
              smokingPolicy.prohibited ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </button>
        </div>
        {smokingPolicy.prohibited && (
          <div className="mt-3 pt-3 border-t border-border">
            <label htmlFor="smoking-allowed-areas" className="block mb-1 text-xs font-medium text-foreground">
              Designated Smoking Area(s) <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              id="smoking-allowed-areas"
              type="text"
              data-testid="smoking-allowed-areas"
              value={smokingPolicy.allowedAreas ?? ''}
              onChange={e => updateSmokingPolicy({ allowedAreas: e.target.value || undefined })}
              className={inputClass}
              placeholder='e.g. "Back patio area only"'
            />
            <p className="mt-1 text-xs text-muted-foreground">If left blank, no smoking areas are permitted anywhere on the property.</p>
          </div>
        )}
      </div>

      {/* Renters Insurance */}
      <div className={sectionClass}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-foreground">Renters Insurance</div>
            <div className="text-xs text-muted-foreground">Require tenants to carry renters insurance?</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={rentersInsurance.required}
            data-testid="renters-insurance-toggle"
            onClick={() => updateRentersInsurance({ required: !rentersInsurance.required, minLiabilityAmount: undefined })}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
              rentersInsurance.required ? 'bg-primary' : 'bg-input'
            }`}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ${
              rentersInsurance.required ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </button>
        </div>
        {rentersInsurance.required && (
          <div className="mt-3 pt-3 border-t border-border">
            <label htmlFor="insurance-min-liability" className="block mb-1 text-xs font-medium text-foreground">
              Minimum Liability Coverage ($) <span className="text-muted-foreground font-normal">(optional, defaults to $100,000)</span>
            </label>
            <input
              id="insurance-min-liability"
              type="number"
              data-testid="insurance-min-liability"
              value={rentersInsurance.minLiabilityAmount ?? ''}
              min={0}
              step={1000}
              onChange={e => updateRentersInsurance({ minLiabilityAmount: e.target.value ? Number(e.target.value) : undefined })}
              className={inputClass}
              placeholder="e.g. 100000"
            />
          </div>
        )}
        {!rentersInsurance.required && (
          <p className="mt-2 text-xs text-muted-foreground">Tenants will be encouraged but not required to carry renters insurance.</p>
        )}
      </div>

      {/* Guest Policy */}
      <div>
        <label htmlFor="guest-policy" className={labelClass}>
          Guest Policy <span className="text-muted-foreground font-normal">(optional)</span>
        </label>
        <textarea
          id="guest-policy"
          value={guestPolicy}
          onChange={e => updateInputs({ guestPolicy: e.target.value || undefined })}
          className={textAreaClass}
          placeholder="e.g. Guests may not stay longer than 14 consecutive days..."
        />
      </div>

      {/* Alterations Policy */}
      <div>
        <label htmlFor="alterations-policy" className={labelClass}>
          Alterations Policy <span className="text-muted-foreground font-normal">(optional)</span>
        </label>
        <textarea
          id="alterations-policy"
          value={alterationsPolicy}
          onChange={e => updateInputs({ alterationsPolicy: e.target.value || undefined })}
          className={textAreaClass}
          placeholder="e.g. Tenant may not make alterations without written consent..."
        />
      </div>
    </div>
  );
}
